import React, { useState, useMemo, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import 'echarts-wordcloud';

// 用户指定的颜色面板
const WORD_CLOUD_COLORS = ['#4e659b', '#8a8cbf', '#b8a8cf', '#e7bcc6', '#fdcf9e', '#efa484', '#b6766c'];

const WordCloudInstance = ({ brand, title, words, onWordClick }) => {
  const option = useMemo(() => {
    return {
      tooltip: {
        show: true,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#e2e8f0',
        textStyle: { color: '#1e293b' },
        formatter: function(item) {
          const w = words.find(x => x.text === item.name);
          const sentimentLabel = w?.sentiment === 'positive' ? '正向' : w?.sentiment === 'negative' ? '负向' : '中性';
          const themeLabel = w?.theme || '综合';
          return `<div style="font-weight:bold;margin-bottom:4px;">${item.name}</div>
                  <div style="font-size:12px;color:#64748b;">
                    权重: <span style="color:#0f172a;font-weight:bold;">${item.value}</span><br/>
                    情感: ${sentimentLabel}<br/>
                    维度: ${themeLabel}
                  </div>`;
        }
      },
      series: [{
        type: 'wordCloud',
        shape: 'circle',
        keepAspect: true,
        left: 'center',
        top: 'center',
        width: '95%',
        height: '95%',
        right: null,
        bottom: null,
        sizeRange: [14, 55], 
        rotationRange: [-45, 90], 
        rotationStep: 45, 
        gridSize: 8, 
        drawOutOfBound: false,
        layoutAnimation: true,
        textStyle: {
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontWeight: 'bold',
          color: function () {
            return WORD_CLOUD_COLORS[Math.floor(Math.random() * WORD_CLOUD_COLORS.length)];
          }
        },
        emphasis: {
          focus: 'self',
          textStyle: {
            shadowBlur: 10,
            shadowColor: '#cbd5e1'
          }
        },
        data: words.map(w => ({
          name: w.text,
          value: w.value,
          sentiment: w.sentiment,
          theme: w.theme
        }))
      }]
    };
  }, [words]);

  const onEvents = {
    click: (params) => {
      const w = words.find(x => x.text === params.name);
      if (w && onWordClick) {
        onWordClick(w.theme, w.sentiment, brand);
      }
    }
  };

  return (
    <div className="flex flex-col bg-slate-50/50 dark:bg-slate-700/20 rounded-lg p-4 border border-slate-100 dark:border-slate-700/50 h-full">
      <h4 className="text-center font-bold text-slate-700 dark:text-slate-300 mb-2">{title || brand}</h4>
      <div className="w-full h-[320px] flex items-center justify-center">
        {words.length > 0 ? (
          <ReactECharts
            option={option}
            onEvents={onEvents}
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'canvas' }}
          />
        ) : (
          <div className="text-slate-400 text-sm flex flex-col items-center gap-2">
            <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            暂无数据
          </div>
        )}
      </div>
    </div>
  );
};

const WordCloudCard = ({ data, brands, onWordClick }) => {
  const ownBrand = brands && brands.length > 0 ? brands[0] : '';
  const competitors = brands && brands.length > 1 ? brands.slice(1) : [];
  
  const [activeCompetitor, setActiveCompetitor] = useState('');

  useEffect(() => {
    if (competitors.length > 0 && (!activeCompetitor || !competitors.includes(activeCompetitor))) {
      setActiveCompetitor(competitors[0]);
    }
  }, [competitors, activeCompetitor]);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">搜索结果词云</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">高频特征提取，点击词汇可溯源对话</p>
        </div>
        {competitors.length > 0 && (
          <div className="flex gap-2 flex-wrap justify-end">
            {competitors.map(b => (
              <button
                key={b}
                onClick={() => setActiveCompetitor(b)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeCompetitor === b 
                  ? 'bg-[#2996cf] text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                {b}
              </button>
            ))}
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {ownBrand && (
          <WordCloudInstance 
            brand={ownBrand}
            title={`本品: ${ownBrand}`}
            words={(data && data[ownBrand]) ? data[ownBrand].slice(0, 50) : []} 
            onWordClick={onWordClick} 
          />
        )}
        
        {activeCompetitor && (
          <WordCloudInstance 
            brand={activeCompetitor}
            title={`竞品: ${activeCompetitor}`}
            words={(data && data[activeCompetitor]) ? data[activeCompetitor].slice(0, 50) : []} 
            onWordClick={onWordClick} 
          />
        )}
      </div>
    </div>
  );
};

export default WordCloudCard;